/*
 * START CUSTOM 2026-08-25-A
 *
 * Linked Variant Options for Dawn 16
 *
 * PHASE 1:
 * HIDE_SOLD_OUT = false
 * Hide non-existent combinations only.
 *
 * PHASE 2:
 * HIDE_SOLD_OUT = true
 * Also hide sold-out combinations.
 */

(() => {
  'use strict';

  // =========================================================
  // CONFIG
  // =========================================================

  // PHASE 1: keep false
  // PHASE 2: change to true
  const HIDE_SOLD_OUT = true;

  const PICKER_SELECTOR = 'variant-selects';
  const GROUP_SELECTOR =
    'fieldset.product-form__input, .product-form__input--dropdown';

  // =========================================================
  // VARIANT DATA
  // =========================================================

  function getVariants(picker) {
    const json = picker.querySelector('[data-product-variants]');

    if (!json) return [];

    try {
      return JSON.parse(json.textContent);
    } catch (error) {
      console.error('[Variant Combinations] Invalid variant JSON', error);
      return [];
    }
  }

  function getVariantOptions(variant) {
    // Modern Shopify JSON
    if (Array.isArray(variant.options)) {
      return variant.options.map(String);
    }

    // Compatibility fallback
    return [
      variant.option1,
      variant.option2,
      variant.option3
    ]
      .filter((value) => value !== null && value !== undefined)
      .map(String);
  }

  function variantAllowed(variant) {
    if (!HIDE_SOLD_OUT) return true;

    return variant.available === true;
  }

  /*
   * Returns true when at least one real variant begins
   * with the supplied option path.
   *
   * Examples:
   * ["Wood"]
   * ["Wood", "Rosewood"]
   */
  function combinationExists(variants, values) {
    return variants.some((variant) => {
      if (!variantAllowed(variant)) return false;

      const variantOptions = getVariantOptions(variant);

      return values.every(
        (value, index) =>
          variantOptions[index] === String(value)
      );
    });
  }

  // =========================================================
  // OPTION GROUP HELPERS
  // =========================================================

  function getGroups(picker) {
    return Array.from(
      picker.querySelectorAll(GROUP_SELECTOR)
    );
  }

  function isRadioGroup(group) {
    return group.matches('fieldset');
  }

  function getControls(group) {
    if (isRadioGroup(group)) {
      return Array.from(
        group.querySelectorAll('input[type="radio"]')
      );
    }

    const select = group.querySelector('select');

    return select
      ? Array.from(select.options)
      : [];
  }

  function getSelectedControl(group) {
    if (isRadioGroup(group)) {
      return group.querySelector(
        'input[type="radio"]:checked'
      );
    }

    const select = group.querySelector('select');

    if (!select || select.selectedIndex < 0) {
      return null;
    }

    return select.options[select.selectedIndex];
  }

  function getLabel(group, input) {
    if (input.labels?.length) {
      return input.labels[0];
    }

    if (!input.id) return null;

    try {
      return group.querySelector(
        `label[for="${CSS.escape(input.id)}"]`
      );
    } catch {
      return null;
    }
  }

  // =========================================================
  // SHOW / HIDE
  // =========================================================

  function setVisible(group, control, visible) {
  if (isRadioGroup(group)) {
    const label = getLabel(group, control);

    if (label) {
      // Dawn explicitly sets variant labels to display:inline-block,
      // so control display directly instead of using label.hidden.
      label.style.display = visible ? '' : 'none';
    }
  } else {
    // Dropdown option
    control.hidden = !visible;
  }

  /*
   * Only remove disabled states created by THIS script.
   * Dawn's own .disabled class remains untouched.
   */
  if (!visible) {
    if (!control.disabled) {
      control.disabled = true;
      control.dataset.linkedVariantDisabled = 'true';
    }
  } else if (
    control.dataset.linkedVariantDisabled === 'true'
  ) {
    control.disabled = false;
    delete control.dataset.linkedVariantDisabled;
  }
}

  // =========================================================
  // SELECT A CONTROL
  // =========================================================

  function selectControl(group, control) {
    if (isRadioGroup(group)) {
      control.checked = true;
      return;
    }

    const select = group.querySelector('select');

    if (!select) return;

    /*
     * Dawn 16 reads select option[selected],
     * not only select.value.
     */
    Array.from(select.options).forEach((option) => {
      option.removeAttribute('selected');
    });

    control.selected = true;
    control.setAttribute('selected', 'selected');
    select.value = control.value;
  }

  // =========================================================
  // MAIN LOGIC
  // =========================================================

  function normalizePicker(picker) {
    const variants = getVariants(picker);
    const groups = getGroups(picker);

    if (!variants.length || !groups.length) {
      return;
    }

    const prefix = [];

    groups.forEach((group) => {
      const controls = getControls(group);

      if (!controls.length) return;

      const allowedControls = [];

      /*
       * Determine valid values using ONLY selections
       * from preceding option groups.
       */
      controls.forEach((control) => {
        const candidate = [
          ...prefix,
          control.value
        ];

        const allowed = combinationExists(
          variants,
          candidate
        );

        setVisible(group, control, allowed);

        if (allowed) {
          allowedControls.push(control);
        }
      });

      let selected = getSelectedControl(group);

      const selectedIsValid =
        selected &&
        combinationExists(
          variants,
          [
            ...prefix,
            selected.value
          ]
        );

      /*
       * Critical part:
       *
       * Leather + Coral
       * User clicks Wood
       *
       * Coral is not valid under Wood,
       * so BEFORE Dawn processes the event:
       *
       * Wood + Rosewood
       */
      if (
        !selectedIsValid &&
        allowedControls.length
      ) {
        selected = allowedControls[0];
        selectControl(group, selected);
      }

      if (selected) {
        prefix.push(selected.value);
      }
    });
  }

  // =========================================================
  // INSTALL
  // =========================================================

  function installPicker(picker) {
    if (
      !picker ||
      picker.dataset.linkedVariantsInstalled === 'true'
    ) {
      return;
    }

    if (!picker.querySelector('[data-product-variants]')) {
      return;
    }

    picker.dataset.linkedVariantsInstalled = 'true';

    /*
     * CAPTURE PHASE is the important part.
     *
     * Browser changes the clicked radio first.
     *
     * Then:
     *
     * 1. Our capture listener runs.
     * 2. We repair all dependent options.
     * 3. Dawn's own normal change listener runs.
     * 4. Dawn sees the corrected combination.
     *
     * We do NOT stop the event.
     * We do NOT dispatch another change event.
     */
    picker.addEventListener(
      'change',
      () => {
        normalizePicker(picker);
      },
      true
    );

    normalizePicker(picker);
  }

  function installAll(root = document) {
    if (
      root instanceof Element &&
      root.matches(PICKER_SELECTOR)
    ) {
      installPicker(root);
    }

    root.querySelectorAll?.(PICKER_SELECTOR)
      .forEach(installPicker);
  }

  // Initial page
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => installAll()
    );
  } else {
    installAll();
  }

  /*
   * Dawn replaces <variant-selects> after each
   * server-side variant update.
   *
   * Install the capture handler on the NEW picker.
   */
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches(PICKER_SELECTOR)) {
          installPicker(node);
        }

        node.querySelectorAll?.(PICKER_SELECTOR)
          .forEach(installPicker);
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Theme editor support
  document.addEventListener(
    'shopify:section:load',
    (event) => installAll(event.target)
  );
})();

/*
 * END CUSTOM 2026-08-25-A
 */